<template>
    <v-container fluid>
        <h1 class="text-h5 mb-4">
            {{ $t('№') }} {{ prescription.prescription_number }} {{ $t('Предписание на подготовку теплового хозяйства к осенне-зимнему периоду ') }}
            {{ prescription.formed_period }} {{ $t('гг.') }}
        </h1>
        <v-card-text v-if="prescription">
            <v-row>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Договор №') }}:</strong> {{ prescription.object_contracts ? prescription.object_contracts[0].contract_number : '' }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Наименование предприятия (КСК)') }}:</strong>
                    {{
                        prescription.ksk_houses
                            ? prescription.ksk_houses[0].ksk_name___ru
                            : prescription.object_contracts
                            ? prescription.object_contracts[0].contractor_name___ru
                            : ''
                    }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Ф.И.О.') }}:</strong> {{ prescription.object_contracts ? prescription.object_contracts[0].fio : '' }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Наименование объекта') }}:</strong>
                    {{ prescription.object_contracts ? prescription.object_contracts[0].object_name___ru : '' }}
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Адрес') }}:</strong>
                    {{
                        prescription.object_contracts
                            ? prescription.object_contracts[0].address_full_name___ru
                            : prescription.ksk_houses
                            ? prescription.ksk_houses[0].address_full_name___ru
                            : ''
                    }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Телефон') }}:</strong> {{ prescription.object_contracts ? prescription.object_contracts[0].phone : '' }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Район города') }}:</strong>
                    {{
                        prescription.ksk_houses
                            ? prescription.ksk_houses[0].district_name___ru
                            : prescription.object_contracts
                            ? prescription.object_contracts[0].district_name___ru
                            : ''
                    }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Участок №') }}:</strong>
                    {{
                        prescription.ksk_houses
                            ? prescription.ksk_houses[0].explotive_area_name
                            : prescription.object_contracts
                            ? prescription.object_contracts[0].explotive_area_name
                            : ''
                    }}
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <strong>{{ $t('№ предписания') }}:</strong> {{ prescription.prescription_number }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Дата формирования') }}:</strong> {{ formatDate(prescription.formed_date) }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Дата выдачи') }}:</strong> {{ formatDate(prescription.issue_date) }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Период') }}:</strong> {{ prescription.formed_period }}
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Статус предписания') }}:</strong> {{ prescription.status_type_name___ru }}
                </v-col>
            </v-row>

            <v-row v-if="showSignedBanner">
                <v-col cols="12">
                    <div class="signed-hint d-flex">
                        <div class="signed-hint__icon d-flex align-center justify-center">
                            <v-icon small>mdi-exclamation-thick</v-icon>
                        </div>
                        <div class="signed-hint__text">
                            Чтобы сформировать акт готовности, просмотрите и примите фактические даты исполнения и документы в организационных, технических и
                            дополнительных мероприятиях.
                        </div>
                    </div>
                </v-col>
            </v-row>
            <v-row v-if="!hasOrgOrTech">
                <v-col cols="12">
                    <div class="signed-hint d-flex">
                        <div class="signed-hint__icon d-flex align-center justify-center">
                            <v-icon small>mdi-exclamation-thick</v-icon>
                        </div>
                        <div class="signed-hint__text">
                            Выдача предписания возможна только при наличии организационных и/или технических мероприятий
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-card-text>

        <v-card-text v-else>
            <v-progress-circular indeterminate color="gray"></v-progress-circular>
            {{ $t('Загрузка...') }}
        </v-card-text>

        <v-card v-if="hasDocuments" outlined class="pa-4 mb-6">
            <div class="text-subtitle-1 font-weight-medium mb-3">
                {{ $t('Документы') }}
            </div>

            <v-row>
                <v-col
                    cols="12"
                    v-for="(doc, idx) in prescription.req_documents"
                    :key="doc.record_id || doc.attachment_link_rid || doc.attach_id || `${doc.attach_file_name}-${idx}`"
                >
                    <div class="d-flex align-center flex-wrap">
                        <v-icon size="18" class="mr-2" color="gray">mdi-file</v-icon>
                        <span class="doc-filename mr-2">{{ getPrescriptionDocDisplayName(doc) }}</span>

                        <v-btn icon class="mr-1" :title="$t('Просмотр')" @click="previewDoc(doc)">
                            <v-icon small>mdi-eye</v-icon>
                        </v-btn>
                        <v-btn icon :title="$t('Скачать')" @click="downloadDoc(doc)">
                            <v-icon small>mdi-download</v-icon>
                        </v-btn>
                    </div>
                </v-col>
            </v-row>
        </v-card>

        <v-card outlined class="pa-4 mb-6">
            <div class="text-subtitle-1 font-weight-medium mb-3">
                {{ $t('Примечание') }}
            </div>

            <v-btn
                v-if="!viewOnly"
                icon
                size="small"
                color="primary"
                class="note-fab rounded-circle"
                @click="openNoteDialog"
                :disabled="uiDisabled || !isDocumentReady"
            >
                <v-icon>mdi-plus</v-icon>
            </v-btn>

            <div v-if="prescription && prescription.note" class="text-body-2">
                {{ prescription.note }}
            </div>
        </v-card>

        <v-dialog v-model="dialogReturn" max-width="560">
            <v-card>
                <v-card-title class="text-h6" color="gray">Укажите причину возврата</v-card-title>
                <v-card-text>
                    <v-textarea
                        v-model="returnReason"
                        color="gray"
                        variant="outlined"
                        rows="4"
                        auto-grow
                        label="Причина"
                        :maxlength="200"
                        counter
                        hide-details="auto"
                    />
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn variant="outlined" color="gray" @click="dialogReturn = false">Отмена</v-btn>
                    <v-btn
                        color="red darken-1"
                        :loading="actionLoading"
                        :disabled="!returnReason.trim() || returnReason.trim().length > 200"
                        @click="submitReturn"
                    >
                        Вернуть
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- коммент при отправки в Smart ЖКХ -->
        <v-dialog v-model="dialogSendToSmartZhkh" max-width="560">
            <v-card>
                <v-card-title class="text-h6" color="gray">Укажите комментарий</v-card-title>
                <v-card-text>
                    <v-textarea
                        v-model="smartZhkhReason"
                        color="gray"
                        variant="outlined"
                        rows="4"
                        auto-grow
                        label="Комментарий"
                        :maxlength="200"
                        counter
                        hide-details="auto"
                    />
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn variant="outlined" color="gray" @click="dialogSendToSmartZhkh = false">
                        Отмена
                    </v-btn>
                    <v-btn
                        color="gray"
                        class="white--text"
                        :loading="actionLoading"
                        :disabled="!smartZhkhReason.trim() || smartZhkhReason.trim().length > 200"
                        @click="submitSendToSmartZhkh"
                    >
                        Отправить
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Подтверждение закрытия -->
        <v-dialog v-model="dialogCloseConfirm" max-width="560">
            <v-card>
                <v-card-title class="text-h6">Подтверждение</v-card-title>
                <v-card-text>
                    Вы уверены, что хотите закрыть предписание? После закрытия восстановить предписание будет невозможно.
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn variant="outlined" color="gray" @click="dialogCloseConfirm = false">
                        Отмена
                    </v-btn>
                    <v-btn color="red darken-1" @click="confirmCloseStep">
                        Да
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Причина закрытия -->
        <v-dialog v-model="dialogCloseReason" max-width="640">
            <v-card>
                <v-card-title class="text-h6">Укажите причину закрытия</v-card-title>
                <v-card-text>
                    <v-radio-group v-model="closeReasonType" color="grey darken-3">
                        <v-radio label="Переход на газ" value="Переход на газ" color="grey darken-3" />
                        <v-radio label="Дом снесен" value="Дом снесен" color="grey darken-3" />
                        <v-radio label="ЧЖД" value="ЧЖД" color="grey darken-3" />
                        <v-radio label="Бесхозный жилой дом" value="Бесхозный жилой дом" color="grey darken-3" />
                        <v-radio label="МЖД" value="МЖД" color="grey darken-3" />
                        <v-radio label="Свой вариант" value="custom" color="grey darken-3" />
                    </v-radio-group>

                    <v-textarea
                        v-if="closeReasonType === 'custom'"
                        v-model="closeReasonCustom"
                        class="mt-2"
                        color="gray"
                        variant="outlined"
                        rows="4"
                        auto-grow
                        label="Введите причину"
                        :maxlength="200"
                        counter
                        hide-details="auto"
                    />
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn variant="outlined" color="gray" @click="dialogCloseReason = false">
                        Отмена
                    </v-btn>
                    <v-btn color="gray" class="white--text" :loading="actionLoading" :disabled="!canSubmitClose" @click="submitClose">
                        Сохранить
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogNote" max-width="640">
            <v-card class="note-dialog-card">
                <v-card-text class="pt-6">
                    <div class="text-subtitle-1 mb-3">{{ $t('Введите примечание') }}</div>

                    <v-textarea
                        v-model="noteDraft"
                        variant="outlined"
                        rows="5"
                        auto-grow
                        :label="$t('Текст примечания')"
                        class="note-textarea"
                        :maxlength="4000"
                        :counter="4000"
                        color="gray"
                    />

                    <div class="d-flex justify-end mt-4">
                        <v-btn variant="outlined" color="gray" class="rounded-pill mr-3 px-6" @click="dialogNote = false">
                            {{ $t('ОТМЕНА') }}
                        </v-btn>

                        <v-btn color="gray" class="rounded-pill px-6" :disabled="!canSaveNote" @click="saveNote">
                            {{ $t('СОХРАНИТЬ') }}
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-card outlined class="pa-4 mb-6" v-if="sortedStatusHistory.length">
            <div class="text-subtitle-1 font-weight-medium mb-3">
                {{ $t('События предписания') }}
            </div>

            <v-row>
                <v-col cols="12" v-for="s in sortedStatusHistory" :key="s.status_date + '-' + s.status_code_name" class="py-2">
                    <div class="status-row">
                        <div class="status-left">
                            <v-chip
                                :color="getStatusMeta(s.status_code_name).chip"
                                :text-color="getStatusMeta(s.status_code_name).text"
                                outlined
                                small
                                class="font-weight-bold"
                            >
                                <v-icon small left :color="getStatusMeta(s.status_code_name).text">
                                    {{ getStatusMeta(s.status_code_name).icon }}
                                </v-icon>
                                {{ s.status_type_name___ru }}
                            </v-chip>

                            <span class="text-caption text--secondary mt-1 d-block">
                                {{ formatStatusDate(s.status_date) }}
                            </span>
                        </div>

                        <div v-if="isStatusWithNote(s)" class="status-note" :class="getStatusNoteClass(s)">
                            <div class="status-note__text">
                                {{ s.note }}
                            </div>
                            <div class="status-note__time">
                                {{ formatStatusDate(s.status_date) }}
                            </div>
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-card>

        <!--группированный список (до подписания) -->
        <v-card outlined class="pa-4 mb-6" v-if="!isSignedStrict && groupedEventsInOrder.length">
            <v-expansion-panels multiple v-model="expandedPanels">
                <v-expansion-panel v-for="grp in groupedEventsInOrder" :key="grp.title">
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon small class="mr-2" color="gray">mdi-chevron-down</v-icon>
                            <span class="group-header">{{ grp.title }}</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                        <v-row
                            v-for="(event, idx) in grp.events"
                            :key="`grp-${grp.title}-${event.event_type_rid || event.prescription_event_rid || idx}`"
                            class="mb-2"
                        >
                            <v-col cols="12" md="11">
                                <v-icon small class="mr-1" color="gray">mdi-calendar-check-outline</v-icon>
                                <span class="mr-2">{{ event.event_type_name___ru }}</span>

                                <v-chip
                                    v-if="event.contract !== null && event.contract === true"
                                    small
                                    color="grey lighten-3"
                                    text-color="black"
                                    class="mr-2"
                                    title="Заявка на продление договора"
                                >
                                    <v-icon x-small left>mdi-check-bold</v-icon>
                                    Заявка на продление договора
                                </v-chip>

                                <v-chip
                                    v-if="event.pute !== null && event.pute === true"
                                    small
                                    color="grey lighten-3"
                                    text-color="black"
                                    title="Заявка на расчет дроссельного устройства"
                                >
                                    <v-icon x-small left>mdi-check-bold</v-icon>
                                    Заявка на расчет дроссельного устройства
                                </v-chip>
                                <div v-if="event.contact_info && parseContactInfo(event.contact_info)" class="ozp-contact-info">
                                    <v-icon x-small class="mr-1" color="grey">
                                        mdi-account-tie
                                    </v-icon>

                                    <strong>Ответственный по ОЗП:</strong>

                                    <span class="ml-1">
                                        {{ parseContactInfo(event.contact_info).name }}
                                        <template v-if="parseContactInfo(event.contact_info).phone">
                                            ({{ parseContactInfo(event.contact_info).phone }})
                                        </template>
                                    </span>
                                </div>
                            </v-col>
                            <v-col cols="12" md="1">
                                <v-chip color="grey lighten-3" text-color="black" small>
                                    <span>{{ formatDate(event.dead_line_plan) }}</span>
                                </v-chip>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-card>

        <!-- Таблицы после подписания -->
        <v-card outlined class="pa-4 mb-6" v-else>
            <div class="text-subtitle-1 font-weight-medium mb-3">Мероприятия</div>

            <!-- Организационные -->
            <v-expansion-panels multiple v-model="expandedSections">
                <v-expansion-panel v-if="orgEvents.length">
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon small class="mr-2" color="gray">mdi-clipboard-text-outline</v-icon>
                            <span class="group-header">Организационные мероприятия</span>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-simple-table dense class="events-table">
                            <colgroup>
                                <col class="col-name" />
                                <col class="col-plan" />
                                <col class="col-fact" />
                                <col class="col-docs" />
                                <col class="col-actions" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Наименование мероприятия</th>
                                    <th>Плановый срок</th>
                                    <th>Фактический срок</th>
                                    <th class="col-docs">Документы</th>
                                    <th class="col-actions col-actions--org"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(ev, idx) in orgEvents" :key="ev.prescription_event_rid || ev.event_type_rid || ev.guid || idx">
                                    <td class="col-name">
                                        <div class="d-flex align-center flex-wrap">
                                            <span class="mr-2">{{ ev.event_type_name___ru }}</span>

                                            <v-chip
                                                v-if="ev.contract !== null && ev.contract === true"
                                                small
                                                color="grey lighten-3"
                                                text-color="black"
                                                class="mr-2"
                                                title="Заявка на продление договора"
                                            >
                                                <v-icon x-small left>mdi-check-bold</v-icon>
                                                Заявка на продление договора
                                            </v-chip>

                                            <v-chip
                                                v-if="ev.pute !== null && ev.pute === true"
                                                small
                                                color="grey lighten-3"
                                                text-color="black"
                                                title="Заявка на расчет дроссельного устройства"
                                            >
                                                <v-icon x-small left>mdi-check-bold</v-icon>
                                                Заявка на расчет дроссельного устройства
                                            </v-chip>
                                            <div v-if="ev.contact_info && parseContactInfo(ev.contact_info)" class="ozp-contact-info">
                                                <v-icon x-small class="mr-1" color="grey">
                                                    mdi-account-tie
                                                </v-icon>

                                                <strong>Ответственный по ОЗП:</strong>

                                                <span class="ml-1">
                                                    {{ parseContactInfo(ev.contact_info).name }}
                                                    <template v-if="parseContactInfo(ev.contact_info).phone">
                                                        ({{ parseContactInfo(ev.contact_info).phone }})
                                                    </template>
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{{ formatDate(ev.dead_line_plan) }}</td>
                                    <td style="min-width: 160px">{{ ev.dead_line_fact ? formatDate(ev.dead_line_fact) : '—' }}</td>

                                    <td style="min-width: 240px">
                                        <div v-if="Array.isArray(ev.attachments) && ev.attachments.length" class="docs-list">
                                            <div v-for="att in ev.attachments" :key="getAttachmentKey(att)" class="doc-row">
                                                <div class="doc-name">
                                                    <v-icon size="18" class="mr-2 mt-1" color="gray">mdi-file</v-icon>

                                                    <div class="doc-meta">
                                                        <div class="truncate">
                                                            {{ att.attach_file_name }}
                                                        </div>

                                                        <div v-if="att.attach_date" class="doc-date">
                                                            {{ formatStatusDate(att.attach_date) }}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="doc-actions">
                                                    <v-btn icon class="mr-1" :title="$t('Просмотр')" @click="previewEventAttachment(att)">
                                                        <v-icon small>mdi-eye</v-icon>
                                                    </v-btn>
                                                    <v-btn icon :title="$t('Скачать')" @click="downloadEventAttachment(att)">
                                                        <v-icon small>mdi-download</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-else-if="ev.attach_file_name" class="docs-list">
                                            <div class="doc-row">
                                                <div class="doc-name">
                                                    <v-icon size="18" class="mr-2" color="gray">mdi-file</v-icon>
                                                    <span class="truncate">{{ ev.attach_file_name }}</span>
                                                </div>
                                                <div class="doc-actions">
                                                    <v-btn icon class="mr-1" :title="$t('Просмотр')" @click="previewEventAttachment(ev)">
                                                        <v-icon small>mdi-eye</v-icon>
                                                    </v-btn>
                                                    <v-btn icon :title="$t('Скачать')" @click="downloadEventAttachment(ev)">
                                                        <v-icon small>mdi-download</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </div>

                                        <span v-else>—</span>
                                    </td>

                                    <td class="actions-cell actions-cell--org">
                                        <template v-if="isEventExecuted(ev)">
                                            <v-icon color="green darken-1">mdi-check-circle</v-icon>
                                        </template>

                                        <template v-else-if="isEventReturned(ev)">
                                            <v-icon color="amber darken-2" :title="$t('Возвращено')">
                                                mdi-arrow-left-circle-outline
                                            </v-icon>
                                        </template>

                                        <template v-else-if="isEventSended(ev) && !viewOnly && !isEventClosed(ev) && currentStatusCode !== 'closed'">
                                            <div class="actions-wrap">
                                                <v-btn small color="green darken-1" @click="acceptOrgEvent(ev)" :disabled="uiDisabled">
                                                    {{ $t('ПРИНЯТЬ') }}
                                                </v-btn>
                                                <v-btn small color="red darken-1" outlined @click="returnOrgEvent(ev)" :disabled="uiDisabled">
                                                    {{ $t('ВЕРНУТЬ') }}
                                                </v-btn>
                                            </div>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Технические -->
                <v-expansion-panel v-if="techEvents.length">
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon small class="mr-2" color="gray">mdi-wrench-outline</v-icon>
                            <span class="group-header">Технические мероприятия</span>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-simple-table dense class="events-table">
                            <colgroup>
                                <col class="col-name" />
                                <col class="col-plan" />
                                <col class="col-fact" />
                                <col class="col-docs" />
                                <col class="col-actions" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Наименование мероприятия</th>
                                    <th>Плановый срок</th>
                                    <th>Фактический срок</th>
                                    <th class="col-docs">Документы</th>
                                    <th class="col-actions col-actions--org"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(ev, idx) in techEvents" :key="ev.prescription_event_rid || ev.event_type_rid || ev.guid || idx">
                                    <td class="col-name">
                                        <div class="d-flex align-center flex-wrap">
                                            <span class="mr-2">{{ ev.event_type_name___ru }}</span>

                                            <v-chip
                                                v-if="ev.contract !== null && ev.contract === true"
                                                small
                                                color="grey lighten-3"
                                                text-color="black"
                                                class="mr-2"
                                                title="Заявка на продление договора"
                                            >
                                                <v-icon x-small left>mdi-check-bold</v-icon>
                                                Заявка на продление договора
                                            </v-chip>

                                            <v-chip
                                                v-if="ev.pute !== null && ev.pute === true"
                                                small
                                                color="grey lighten-3"
                                                text-color="black"
                                                title="Заявка на расчет дроссельного устройства"
                                            >
                                                <v-icon x-small left>mdi-check-bold</v-icon>
                                                Заявка на расчет дроссельного устройства
                                            </v-chip>
                                        </div>
                                    </td>

                                    <!-- Плановый срок -->
                                    <td>{{ formatDate(ev.dead_line_plan) }}</td>

                                    <!-- Фактический срок -->
                                    <td style="min-width: 160px">
                                        {{ ev.dead_line_fact ? formatDate(ev.dead_line_fact) : '—' }}
                                    </td>

                                    <!-- Документы -->
                                    <td style="min-width: 240px">
                                        <div v-if="Array.isArray(ev.attachments) && ev.attachments.length" class="docs-list">
                                            <div v-for="att in ev.attachments" :key="getAttachmentKey(att)" class="doc-row">
                                                <div class="doc-name">
                                                    <v-icon size="18" class="mr-2 mt-1" color="gray">mdi-file</v-icon>

                                                    <div class="doc-meta">
                                                        <div class="truncate">
                                                            {{ att.attach_file_name }}
                                                        </div>

                                                        <div v-if="att.attach_date" class="doc-date">
                                                            {{ formatStatusDate(att.attach_date) }}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="doc-actions">
                                                    <v-btn icon class="mr-1" :title="$t('Просмотр')" @click="previewEventAttachment(att)">
                                                        <v-icon small>mdi-eye</v-icon>
                                                    </v-btn>
                                                    <v-btn icon :title="$t('Скачать')" @click="downloadEventAttachment(att)">
                                                        <v-icon small>mdi-download</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-else-if="ev.attach_file_name" class="docs-list">
                                            <div class="doc-row">
                                                <div class="doc-name">
                                                    <v-icon size="18" class="mr-2" color="gray">mdi-file</v-icon>
                                                    <span class="truncate">{{ ev.attach_file_name }}</span>
                                                </div>
                                                <div class="doc-actions">
                                                    <v-btn icon class="mr-1" :title="$t('Просмотр')" @click="previewEventAttachment(ev)">
                                                        <v-icon small>mdi-eye</v-icon>
                                                    </v-btn>
                                                    <v-btn icon :title="$t('Скачать')" @click="downloadEventAttachment(ev)">
                                                        <v-icon small>mdi-download</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </div>

                                        <span v-else>—</span>
                                    </td>

                                    <!-- Действия -->
                                    <td class="actions-cell actions-cell--org">
                                        <template v-if="isEventExecuted(ev)">
                                            <v-icon color="green darken-1">mdi-check-circle</v-icon>
                                        </template>

                                        <template v-else-if="isEventReturned(ev)">
                                            <v-icon color="amber darken-2" :title="$t('Возвращено')">
                                                mdi-arrow-left-circle-outline
                                            </v-icon>
                                        </template>

                                        <template v-else-if="isEventSended(ev) && !viewOnly && !isEventClosed(ev) && currentStatusCode !== 'closed'">
                                            <div class="actions-wrap">
                                                <v-btn small color="green darken-1" @click="acceptOrgEvent(ev)" :disabled="uiDisabled">
                                                    {{ $t('ПРИНЯТЬ') }}
                                                </v-btn>
                                                <v-btn small color="red darken-1" outlined @click="returnOrgEvent(ev)" :disabled="uiDisabled">
                                                    {{ $t('ВЕРНУТЬ') }}
                                                </v-btn>
                                            </div>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Дополнительные мероприятия NEW -->
                <v-expansion-panel v-if="addEvents.length">
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon small class="mr-2" color="gray">mdi-wrench-outline</v-icon>
                            <span class="group-header">Дополнительные мероприятия</span>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-simple-table dense class="events-table">
                            <colgroup>
                                <col class="col-name" />
                                <col class="col-plan" />
                                <col class="col-fact" />
                                <col class="col-docs" />
                                <col class="col-actions" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Наименование мероприятия</th>
                                    <th>Плановый срок</th>
                                    <th>Фактический срок</th>
                                    <th class="col-docs">Документы</th>
                                    <th class="col-actions col-actions--org"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(ev, idx) in addEvents" :key="ev.prescription_event_rid || ev.event_type_rid || ev.guid || idx">
                                    <td class="col-name">
                                        <div class="d-flex align-center flex-wrap">
                                            <span class="mr-2">{{ ev.event_type_name___ru }}</span>
                                        </div>
                                    </td>

                                    <!-- Плановый срок -->
                                    <td>{{ formatDate(ev.dead_line_plan) }}</td>

                                    <!-- Фактический срок -->
                                    <td style="min-width: 160px">
                                        {{ ev.dead_line_fact ? formatDate(ev.dead_line_fact) : '—' }}
                                    </td>

                                    <!-- Документы -->
                                    <td style="min-width: 240px">
                                        <div v-if="Array.isArray(ev.attachments) && ev.attachments.length" class="docs-list">
                                            <div v-for="att in ev.attachments" :key="getAttachmentKey(att)" class="doc-row">
                                                <div class="doc-name">
                                                    <v-icon size="18" class="mr-2 mt-1" color="gray">mdi-file</v-icon>

                                                    <div class="doc-meta">
                                                        <div class="truncate">
                                                            {{ att.attach_file_name }}
                                                        </div>

                                                        <div v-if="att.attach_date" class="doc-date">
                                                            {{ formatStatusDate(att.attach_date) }}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="doc-actions">
                                                    <v-btn icon class="mr-1" :title="$t('Просмотр')" @click="previewEventAttachment(att)">
                                                        <v-icon small>mdi-eye</v-icon>
                                                    </v-btn>
                                                    <v-btn icon :title="$t('Скачать')" @click="downloadEventAttachment(att)">
                                                        <v-icon small>mdi-download</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-else-if="ev.attach_file_name" class="docs-list">
                                            <div class="doc-row">
                                                <div class="doc-name">
                                                    <v-icon size="18" class="mr-2" color="gray">mdi-file</v-icon>
                                                    <span class="truncate">{{ ev.attach_file_name }}</span>
                                                </div>
                                                <div class="doc-actions">
                                                    <v-btn icon class="mr-1" :title="$t('Просмотр')" @click="previewEventAttachment(ev)">
                                                        <v-icon small>mdi-eye</v-icon>
                                                    </v-btn>
                                                    <v-btn icon :title="$t('Скачать')" @click="downloadEventAttachment(ev)">
                                                        <v-icon small>mdi-download</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </div>

                                        <span v-else>—</span>
                                    </td>

                                    <!-- Действия -->
                                    <td class="actions-cell actions-cell--org">
                                        <template v-if="isEventExecuted(ev)">
                                            <v-icon color="green darken-1">mdi-check-circle</v-icon>
                                        </template>

                                        <template v-else-if="isEventReturned(ev)">
                                            <v-icon color="amber darken-2" :title="$t('Возвращено')">
                                                mdi-arrow-left-circle-outline
                                            </v-icon>
                                        </template>

                                        <template
                                            v-else-if="
                                                isEventSended(ev) &&
                                                    !viewOnly &&
                                                    !isEventClosed(ev) &&
                                                    currentStatusCode !== 'closed' &&
                                                    currentStatusCode !== 'issued_act' &&
                                                    currentStatusCode !== 'completed'
                                            "
                                        >
                                            <div class="actions-wrap">
                                                <v-btn small color="green darken-1" @click="acceptOrgEvent(ev)" :disabled="uiDisabled">
                                                    {{ $t('ПРИНЯТЬ') }}
                                                </v-btn>
                                                <v-btn small color="red darken-1" outlined @click="returnOrgEvent(ev)" :disabled="uiDisabled">
                                                    {{ $t('ВЕРНУТЬ') }}
                                                </v-btn>
                                            </div>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Дополнительные OLD-->
                <v-expansion-panel v-if="addEvents.length && false">
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon small class="mr-2" color="gray">mdi-playlist-plus</v-icon>
                            <span class="group-header">Дополнительные мероприятия</span>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-simple-table dense class="events-table">
                            <colgroup>
                                <col class="col-name" />
                                <col class="col-plan" />
                                <col class="col-fact" />
                                <col class="col-docs" />
                                <col class="col-actions" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Наименование мероприятия</th>
                                    <th>Плановый срок</th>
                                    <th>Фактический срок</th>
                                    <th>Документы</th>
                                    <th class="col-actions"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(ev, idx) in addEvents"
                                    :key="ev.prescription_event_rid || ev.event_type_rid || ev.guid || idx"
                                    :data-event-row="ev.prescription_event_rid"
                                    :class="{ 'row-locked': pendingLockId === ev.prescription_event_rid }"
                                >
                                    <td class="col-name">{{ ev.event_type_name___ru }}</td>
                                    <td>{{ formatDate(ev.dead_line_plan) }}</td>
                                    <td style="min-width: 160px">
                                        <template v-if="viewOnly || isEventExecuted(ev)">
                                            <span>{{ ev.dead_line_fact ? formatDate(ev.dead_line_fact) : '—' }}</span>
                                        </template>
                                        <template v-else-if="canEditAdditionalEvents">
                                            <v-text-field
                                                v-model="ensureEdit(ev).dead_line_fact"
                                                type="date"
                                                dense
                                                hide-details="auto"
                                                :min="factMinDate"
                                                :max="factMaxDate"
                                                :error="isFactOutOfBounds(ensureEdit(ev).dead_line_fact)"
                                                :error-messages="isFactOutOfBounds(ensureEdit(ev).dead_line_fact) ? [factDateErrorText] : []"
                                                :disabled="isLockedByOther(ev)"
                                                @focus="onAttemptEdit(ev)"
                                                @update:modelValue="onFactChange(ev, $event)"
                                            />
                                        </template>
                                    </td>

                                    <td style="min-width: 240px">
                                        <template v-if="isEventExecuted(ev)">
                                            <div v-if="Array.isArray(ev.attachments) && ev.attachments.length" class="docs-list">
                                                <div v-for="att in ev.attachments" :key="getAttachmentKey(att)" class="doc-row">
                                                    <div class="doc-name">
                                                        <v-icon size="18" class="mr-2 mt-1" color="gray">mdi-file</v-icon>

                                                        <div class="doc-meta">
                                                            <div class="truncate">
                                                                {{ att.attach_file_name }}
                                                            </div>

                                                            <div v-if="att.attach_date" class="doc-date">
                                                                {{ formatStatusDate(att.attach_date) }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="doc-actions">
                                                        <v-btn icon class="mr-1" :title="$t('Просмотр')" @click="previewEventAttachment(att)">
                                                            <v-icon small>mdi-eye</v-icon>
                                                        </v-btn>
                                                        <v-btn icon :title="$t('Скачать')" @click="downloadEventAttachment(att)">
                                                            <v-icon small>mdi-download</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </div>
                                            <span v-else>—</span>
                                        </template>
                                        <template v-else>
                                            <div v-if="Array.isArray(ev.attachments) && ev.attachments.length" class="docs-list mb-2">
                                                <div v-for="att in ev.attachments" :key="getAttachmentKey(att)" class="doc-row">
                                                    <div class="doc-name">
                                                        <v-icon size="18" class="mr-2 mt-1" color="gray">mdi-file</v-icon>

                                                        <div class="doc-meta">
                                                            <div class="truncate">
                                                                {{ att.attach_file_name }}
                                                            </div>

                                                            <div v-if="att.attach_date" class="doc-date">
                                                                {{ formatStatusDate(att.attach_date) }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="doc-actions">
                                                        <v-btn icon class="mr-1" :title="$t('Просмотр')" @click="previewEventAttachment(att)">
                                                            <v-icon small>mdi-eye</v-icon>
                                                        </v-btn>
                                                        <v-btn icon :title="$t('Скачать')" @click="downloadEventAttachment(att)">
                                                            <v-icon small>mdi-download</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </div>
                                            <v-file-input
                                                v-if="canEditAdditionalEvents"
                                                v-model="ensureEdit(ev).files"
                                                dense
                                                hide-details
                                                multiple
                                                prepend-icon="mdi-paperclip"
                                                :label="$t('Загрузить файлы')"
                                                :disabled="isLockedByOther(ev)"
                                                @click="onAttemptEdit(ev)"
                                            />
                                        </template>
                                    </td>
                                    <td class="actions-cell">
                                        <template v-if="isEventExecuted(ev)">
                                            <v-icon color="green darken-1">mdi-check-circle</v-icon>
                                        </template>
                                        <template v-else>
                                            <v-btn
                                                v-if="canEditAdditionalEvents"
                                                small
                                                color="green darken-1"
                                                :loading="ensureEdit(ev).loading"
                                                :disabled="!ensureEdit(ev).dead_line_fact"
                                                @click="executeEvent(ev)"
                                            >
                                                {{ $t('Исполнить') }}
                                            </v-btn>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-card>

        <v-card-actions class="justify-end">
            <v-btn color="gray" class="white--text" @click="handleBack" :disabled="uiDisabled || !isDocumentReady || backNavLock" :loading="backNavLock">
                {{ $t('Назад') }}
            </v-btn>
            <v-btn
                color="gray"
                class="white--text"
                v-if="!viewOnly && !isSigned && !isReturned && !isChecked"
                @click="openPlannedDeadline"
                :disabled="uiDisabled || !isDocumentReady"
            >
                {{ hasEvents ? $t('Редактировать плановый срок') : $t('Добавить плановый срок') }}
            </v-btn>

            <v-btn
                color="gray"
                class="white--text"
                v-if="!viewOnly && !isSigned && !isReturned && !isChecked"
                @click="openSign"
                :disabled="uiDisabled || !isDocumentReady || !hasOrgOrTech"
            >
                {{ $t('Подписать') }}
            </v-btn>
            <v-btn
                color="gray"
                class="white--text"
                v-if="!viewOnly && isReturned"
                @click="openSendToSmartZhkhDialog"
                :disabled="uiDisabled || !isDocumentReady"
            >
                {{ $t('Отправить в Smart ЖКХ') }}
            </v-btn>
            <v-btn
                color="gray"
                class="white--text"
                v-if="isAdminOZP && isSignedForAct"
                @click="dialogSignAct = true"
                :disabled="uiDisabled || !isDocumentReady"
            >
                {{ $t('Сформировать акт готовности') }}
            </v-btn>
            <!-- Закрытие предписания -->
            <v-tooltip bottom v-if="isSummer && canClosePrescription">
                <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="close-btn-tooltip-wrapper">
                        <v-btn color="gray" class="white--text" disabled>
                            {{ $t('Закрыть предписание') }}
                        </v-btn>
                    </span>
                </template>

                <span>Закрытие предписаний доступно с 1 сентября по 14 апреля</span>
            </v-tooltip>

            <v-btn v-else-if="canClosePrescription" color="gray" class="white--text" @click="openClose" :disabled="uiDisabled || !isDocumentReady">
                {{ $t('Закрыть предписание') }}
            </v-btn>
        </v-card-actions>

        <CreatePlannedDeadline
            :key="plannedDialogKey"
            v-if="!isSigned"
            :model-value="dialogCreatePlannedDeadline"
            @update:modelValue="dialogCreatePlannedDeadline = $event"
            :info-create-model="prescription"
            @saved="loadPrescription"
        />

        <SignPrescription
            :key="signDialogKey"
            v-if="!isSigned"
            :model-value="dialogSignPrescription"
            @update:modelValue="dialogSignPrescription = $event"
            :prescription="prescription"
            @saved="loadPrescription"
        />
        <SignAct
            v-if="isSignedForAct"
            :model-value="dialogSignAct"
            @update:modelValue="dialogSignAct = $event"
            :prescription="prescription"
            @saved="loadPrescription"
        />
        <v-overlay :value="loadingPrescription" opacity="0.08" class="loading-overlay">
            <v-progress-circular indeterminate :size="64" :width="4" color="gray" />
        </v-overlay>
    </v-container>
</template>

<script>
import axios from 'axios';
import CreatePlannedDeadline from '@/components/CreatePlannedDeadline.vue';
import SignPrescription from '@/components/SignPrescription.vue';
import SignAct from '@/components/SignAct.vue';
import PrescriptionOZPService from '@/services/apiRequests/prescription_ozp.service';

export default {
    components: { CreatePlannedDeadline, SignPrescription, SignAct },
    name: 'PrescriptionOZPDetail',
    watch: {
        groupedEventsInOrder: {
            handler(val) {
                if (Array.isArray(val)) {
                    this.expandedPanels = val.map((_, i) => i);
                }
            },
            immediate: true
        },
        isSigned(val) {
            if (val) {
                this.dialogCreatePlannedDeadline = false;
                this.dialogSignPrescription = false;
            }
        }
    },
    methods: {
        parseContactInfo(contactInfo) {
            if (!contactInfo) return null;

            try {
                const data = typeof contactInfo === 'string' ? JSON.parse(contactInfo) : contactInfo;

                const name = data?.contact_name || '';
                const phone = data?.contact_phone || '';

                if (!name && !phone) return null;

                return { name, phone };
            } catch (e) {
                console.error('Ошибка парсинга contact_info:', e);
                return null;
            }
        },
        sortedEvents(events) {
            return [...(events || [])].sort((a, b) => {
                return Number(a.event_type_rid || 0) - Number(b.event_type_rid || 0);
            });
        },
        getStatusNoteClass(s) {
            const code = this.normalizeStatus(s?.status_code_name);

            if (code === 'closed') return 'status-note--closed';
            if (code === 'issued') return 'status-note--issued';

            return 'status-note--returned';
        },

        openClose() {
            if (this.viewOnly) return;
            this.dialogCloseConfirm = true;
        },

        confirmCloseStep() {
            this.dialogCloseConfirm = false;
            this.closeReasonType = '';
            this.closeReasonCustom = '';
            this.dialogCloseReason = true;
        },

        getCloseReasonText() {
            if (this.closeReasonType === 'custom') {
                return (this.closeReasonCustom || '').trim();
            }
            return this.closeReasonType;
        },

        async submitClose() {
            if (this.viewOnly) return;

            const reason = this.getCloseReasonText();
            if (!reason) return;

            try {
                this.actionLoading = true;

                const CLOSED_STATUS_RID = 9; //потом вытащу из справочника

                await PrescriptionOZPService.prescriptionOZPStatusChange(this.prescription.record_id, CLOSED_STATUS_RID, reason);

                this.$toast?.success?.(this.$t('Предписание закрыто'));

                this.dialogCloseReason = false;
                this.closeReasonType = '';
                this.closeReasonCustom = '';

                await this.loadPrescription();
            } catch (e) {
                console.error('Не удалось закрыть предписание:', e);
                this.$toast?.error?.(this.$t('Не удалось закрыть предписание'));
            } finally {
                this.actionLoading = false;
            }
        },
        getPrescriptionDocDisplayName(doc) {
            const original = (doc?.attach_file_name || '').toString();
            const lower = original.toLowerCase();

            if (lower === 'smart-act-print-form.cms') {
                const num = this.prescription?.prescription_number ?? '';
                return `Акт готовности ${num}.pdf`;
            }

            return original;
        },

        async submitSendToSmartZhkh() {
            if (!this.prescription || !this.prescription.record_id) {
                this.$toast?.error?.(this.$t('Не найдено предписание'));
                return;
            }

            const reason = (this.smartZhkhReason || '').trim();
            if (!reason || reason.length > 200) return;

            try {
                this.actionLoading = true;

                const ISSUED_STATUS_RID = 2; // TODO: потом сделаю методом получающий рид статуса

                await PrescriptionOZPService.prescriptionOZPStatusChange(this.prescription.record_id, ISSUED_STATUS_RID, reason);

                this.$toast?.success?.(this.$t('Предписание отправлено в Smart ЖКХ и статус изменён на «Выдано»'));

                this.dialogSendToSmartZhkh = false;
                this.smartZhkhReason = '';

                await this.loadPrescription();
            } catch (e) {
                console.error('Не удалось отправить в Smart ЖКХ:', e);
                this.$toast?.error?.(this.$t('Не удалось изменить статус предписания'));
            } finally {
                this.actionLoading = false;
            }
        },

        async checkAndSetVerified() {
            if (!this.prescription) return;

            const current =
                (this.prescription.status_code_name && String(this.prescription.status_code_name).toLowerCase()) ||
                (this.sortedStatusHistory.length ? String(this.sortedStatusHistory[this.sortedStatusHistory.length - 1].status_code_name).toLowerCase() : null);

            // если verified или  issued_act менять не надо
            if (['checked', 'issued_act', 'completed', 'closed', 'canceled'].includes(current)) return;

            const isTargetGroup = g => ['организационные мероприятия', 'технические мероприятия'].includes(String(g || '').toLowerCase());

            const targetEvents = (this.prescription?.prescription_events || []).filter(e => isTargetGroup(e.event_group_name));

            if (!targetEvents.length) return;

            const allExecuted = targetEvents.every(e => this.isEventExecuted(e));
            if (!allExecuted) return;

            try {
                const VERIFIED_STATUS_RID = 6; //изменить на получение по коду

                await PrescriptionOZPService.prescriptionOZPStatusChange(this.prescription.record_id, VERIFIED_STATUS_RID);

                await this.loadPrescription();
                this.$toast?.success?.(this.$t('Статус предписания изменён на «Проверено»'));
            } catch (e) {
                console.error('Не удалось автоматически установить статус "Проверено":', e);
            }
        },
        isEventReturned(ev) {
            const direct = this.normalizeStatus(ev && ev.status_code_name);
            if (direct) return direct === 'returned';

            const rid = ev ? ev.prescription_event_rid : null;
            const allHist = (this.prescription && this.prescription.prescription_events_history) || [];
            const hist = allHist
                .filter(h => h.prescription_event_rid === rid && h.status_code_name)
                .sort((a, b) => new Date(a.status_date || 0) - new Date(b.status_date || 0));

            const last = hist.length ? this.normalizeStatus(hist[hist.length - 1].status_code_name) : '';
            return last === 'returned';
        },

        onFactChange(ev, val) {
            const ed = this.ensureEdit(ev);
            ed.dead_line_fact = val;

            const norm = x => this.normalizeDateStr(x || '');
            ed._date_changed = norm(ed.dead_line_fact) && norm(ed.dead_line_fact) !== norm(ev.dead_line_fact);
        },
        isLockedByOther(ev) {
            const id = ev?.prescription_event_rid;
            return !!(this.pendingLockId && id && this.pendingLockId !== id);
        },
        onAttemptEdit(ev) {
            if (!this.isLockedByOther(ev)) return;

            const msg = 'Нажмите «Исполнить» для мероприятия с заполненными данными. Внимание! Изменение даты и прикрепленного файла после этого невозможно.';
            this.$toast?.info?.(msg);

            this.$nextTick(() => {
                const lockId = this.pendingLockId;
                const el = this.$el.querySelector(`[data-event-row="${lockId}"]`);
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }
            });
        },
        normalizeDateStr(d) {
            if (!d) return '';
            if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
            if (/^\d{4}-\d{2}-\d{2}T/.test(d)) return d.slice(0, 10);
            const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(d);
            if (m) return `${m[3]}-${m[2]}-${m[1]}`;
            const dt = new Date(d);
            if (!isNaN(dt)) {
                const yyyy = dt.getFullYear();
                const mm = String(dt.getMonth() + 1).padStart(2, '0');
                const dd = String(dt.getDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
            }
            return '';
        },
        isFactOutOfBounds(ymd) {
            const n = this.normalizeDateStr(ymd);
            if (!n) return false;
            return n < this.factMinDate || n > this.factMaxDate;
        },
        handleBack() {
            if (this.backNavLock) return;
            this.backNavLock = true;
            this.$router.back();

            setTimeout(() => {
                this.backNavLock = false;
            }, 1200);
        },
        async openPlannedDeadline() {
            this.dialogSignPrescription = false;
            this.dialogSignAct = false;
            await this.$nextTick();
            this.dialogCreatePlannedDeadline = false;
            this.plannedDialogKey++;
            await this.$nextTick();
            this.dialogCreatePlannedDeadline = true;
        },
        async openSign() {
            this.dialogCreatePlannedDeadline = false;
            this.dialogSignAct = false;
            await this.$nextTick();
            this.dialogSignPrescription = false;
            this.signDialogKey++;
            await this.$nextTick();
            this.dialogSignPrescription = true;
        },
        isStatusWithNote(s) {
            const code = this.normalizeStatus(s?.status_code_name);
            return ['returned', 'closed', 'issued'].includes(code) && !!(s?.note && String(s.note).trim());
        },
        ensureEdit(ev) {
            const id = ev?.prescription_event_rid;
            if (!id) return { dead_line_fact: '', files: [], loading: false };
            if (!this.edits[id]) {
                this.edits = {
                    ...this.edits,
                    [id]: {
                        dead_line_fact: ev.dead_line_fact || '',
                        files: [],
                        loading: false
                    }
                };
            }
            return this.edits[id];
        },
        isEventExecuted(ev) {
            const direct = this.normalizeStatus(ev && ev.status_code_name);
            if (direct) return direct === 'executed';

            const rid = ev ? ev.prescription_event_rid : null;
            const allHist = (this.prescription && this.prescription.prescription_events_history) || [];
            const hist = allHist
                .filter(h => h.prescription_event_rid === rid && h.status_code_name)
                .sort((a, b) => new Date(a.status_date || 0) - new Date(b.status_date || 0));

            const last = hist.length ? this.normalizeStatus(hist[hist.length - 1].status_code_name) : '';
            return last === 'executed';
        },

        isEventClosed(ev) {
            const direct = this.normalizeStatus(ev && ev.status_code_name);
            if (direct) return direct === 'closed';

            const rid = ev ? ev.prescription_event_rid : null;
            const allHist = (this.prescription && this.prescription.prescription_events_history) || [];

            const hist = allHist
                .filter(h => h.prescription_event_rid === rid && h.status_code_name)
                .sort((a, b) => new Date(a.status_date || 0) - new Date(b.status_date || 0));

            const last = hist.length ? this.normalizeStatus(hist[hist.length - 1].status_code_name) : '';
            return last === 'closed';
        },

        async uploadFilesForEvent(ev) {
            const edit = this.ensureEdit(ev);
            if (!Array.isArray(edit.files) || edit.files.length === 0) return;

            const form = new FormData();
            for (const f of edit.files) form.append('file', f);

            const qs = new URLSearchParams();
            if (ev.guid) qs.set('guid', ev.guid);

            await axios.post(`/prescription_ozp/attachmentsLinkInsert?${qs.toString()}`, form);
        },
        async executeEvent(ev) {
            if (this.viewOnly) return;
            try {
                const edit = this.ensureEdit(ev);
                if (!edit.dead_line_fact) {
                    this.$toast?.error?.(this.$t('Укажите фактический срок'));
                    return;
                }

                edit.loading = true;

                const eventRid = this.getEventRid(ev);
                if (!eventRid) throw new Error('Не найден идентификатор мероприятия');

                if (Array.isArray(edit.files) && edit.files.length) {
                    await this.uploadFilesForEvent(ev);
                }

                await this.callEventStatusChange({
                    eventRid,
                    statusRid: 3,
                    dateYmd: edit.dead_line_fact,
                    comment: null
                });

                this.$toast?.success?.(this.$t('Статус изменён на «Исполнено»'));

                this.edits = {
                    ...this.edits,
                    [eventRid]: {
                        ...this.edits[eventRid],
                        files: []
                    }
                };

                await this.loadPrescription();
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось исполнить мероприятие'));
            } finally {
                const id = ev?.prescription_event_rid;
                if (id && this.edits[id]) {
                    this.edits = {
                        ...this.edits,
                        [id]: { ...this.edits[id], loading: false }
                    };
                }
            }
        },
        normalizeStatus(code) {
            return (code ? String(code) : '').trim().toLowerCase();
        },

        isEventSended(ev) {
            const direct = this.normalizeStatus(ev && ev.status_code_name);
            if (direct) return direct === 'sended';

            const rid = ev ? ev.prescription_event_rid : null;
            const allHist = (this.prescription && this.prescription.prescription_events_history) || [];
            const hist = allHist
                .filter(h => h.prescription_event_rid === rid && h.status_code_name)
                .sort((a, b) => new Date(a.status_date || 0) - new Date(b.status_date || 0));

            const last = hist.length ? this.normalizeStatus(hist[hist.length - 1].status_code_name) : '';
            return last === 'sended';
        },

        getAttachmentKey(item) {
            return (item && (item.attachment_link_rid || item.attach_id || item.record_id || item.attach_file_name)) || String(Math.random()).slice(2);
        },

        getAttachmentRecordId(item) {
            if (item && (item.attachment_link_rid || item.attach_id || item.record_id)) {
                return item.attachment_link_rid || item.attach_id || item.record_id;
            }

            const list = item && item.attachments;
            if (Array.isArray(list) && list.length === 1) {
                const att = list[0];
                return att.attachment_link_rid || att.attach_id || att.record_id || null;
            }
            return null;
        },

        getAttachmentFileName(item) {
            if (item && item.attach_file_name) return item.attach_file_name;
            const list = item && item.attachments;
            if (Array.isArray(list) && list.length === 1) {
                return list[0].attach_file_name || 'document';
            }
            return 'document';
        },

        openBlobInNewTab({ url, title = 'Документ' }) {
            const w = window.open('', '_blank');
            if (!w) {
                const a = document.createElement('a');
                a.href = url;
                a.target = '_blank';
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                a.remove();

                setTimeout(() => URL.revokeObjectURL(url), 60000);
                return;
            }

            try {
                w.opener = null;
            } catch (e) {
                void e;
            }

            w.document.title = title;
            w.document.body.style.margin = '0';
            w.document.body.style.padding = '0';
            const iframe = w.document.createElement('iframe');
            iframe.src = url;
            iframe.style.cssText = 'position:fixed;inset:0;border:0;width:100%;height:100%';
            w.document.body.appendChild(iframe);

            const cleanup = () => setTimeout(() => URL.revokeObjectURL(url), 60000);
            iframe.onload = cleanup;

            setTimeout(cleanup, 90000);
        },
        async previewEventAttachment(item) {
            try {
                const recordId = this.getAttachmentRecordId(item);
                if (!recordId) throw new Error('Не найден идентификатор вложения');

                const { blob, fileName, mime } = await this.fetchAttachmentBlob(recordId);
                const name = fileName || this.getAttachmentFileName(item);

                if (
                    (mime && mime.includes('pdf')) ||
                    String(name)
                        .toLowerCase()
                        .endsWith('.pdf')
                ) {
                    const url = URL.createObjectURL(blob);
                    this.openBlobInNewTab({ url, title: name || 'PDF' });
                } else {
                    await this.downloadEventAttachment(item);
                }
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось открыть документ мероприятия'));
            }
        },

        async downloadEventAttachment(item) {
            try {
                const recordId = this.getAttachmentRecordId(item);
                if (!recordId) throw new Error('Не найден идентификатор вложения');

                const { blob, fileName } = await this.fetchAttachmentBlob(recordId);
                const finalName = fileName || this.getAttachmentFileName(item);

                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = finalName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(a.href), 60000);
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось скачать документ мероприятия'));
            }
        },
        toYMD(d) {
            const dt = new Date(d);
            const yyyy = dt.getFullYear();
            const mm = String(dt.getMonth() + 1).padStart(2, '0');
            const dd = String(dt.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        },

        getEventRid(ev) {
            return ev.prescription_event_rid;
        },
        async callEventStatusChange({ eventRid, statusRid, dateYmd, comment }) {
            return axios.get('/prescription_ozp/prescriptionEventStatusChange', {
                params: {
                    iPrescriptionEvent_rid: eventRid,
                    iStatus_rid: statusRid,
                    dDate_id: dateYmd || null,
                    comment: comment || null
                }
            });
        },

        async acceptOrgEvent(ev) {
            if (this.viewOnly) return;
            try {
                this.actionLoading = true;
                const eventRid = this.getEventRid(ev);
                if (!eventRid) throw new Error('Не найден идентификатор мероприятия');

                const today = this.toYMD(new Date());
                const fact = ev.dead_line_fact && String(ev.dead_line_fact).trim() ? ev.dead_line_fact : today;

                await this.callEventStatusChange({
                    eventRid,
                    statusRid: 3,
                    dateYmd: fact,
                    comment: null
                });

                this.$toast?.success?.('Мероприятие принято');
                await this.loadPrescription();
            } catch (e) {
                console.error(e);
                this.$toast?.error?.('Не удалось принять мероприятие');
            } finally {
                this.actionLoading = false;
            }
        },

        returnOrgEvent(ev) {
            if (this.viewOnly) return;
            this.returnTargetEvent = ev;
            this.returnReason = '';
            this.dialogReturn = true;
        },

        async submitReturn() {
            if (this.viewOnly) return;
            if (!this.returnTargetEvent) return;
            try {
                this.actionLoading = true;
                const eventRid = this.getEventRid(this.returnTargetEvent);
                if (!eventRid) throw new Error('Не найден идентификатор мероприятия');

                await this.callEventStatusChange({
                    eventRid,
                    statusRid: 1,
                    dateYmd: null,
                    comment: this.returnReason.trim()
                });

                this.$toast?.info?.('Мероприятие возвращено');
                this.dialogReturn = false;
                this.returnTargetEvent = null;
                await this.loadPrescription();
            } catch (e) {
                console.error(e);
                this.$toast?.error?.('Не удалось вернуть мероприятие');
            } finally {
                this.actionLoading = false;
            }
        },
        getStatusMeta(code) {
            const map = {
                formed: { chip: 'blue lighten-5', text: 'blue darken-2', icon: 'mdi-clock-time-four-outline' },
                issued: { chip: 'blue lighten-5', text: 'blue darken-2', icon: 'mdi-autorenew' },
                signed: { chip: 'green lighten-5', text: 'green darken-2', icon: 'mdi-check-circle-outline' },
                completed: { chip: 'green lighten-5', text: 'green darken-2', icon: 'mdi-check-circle-outline' },
                returned: { chip: 'orange lighten-5', text: 'orange darken-2', icon: 'mdi-arrow-left-circle-outline' },
                canceled: { chip: 'red lighten-5', text: 'red darken-2', icon: 'mdi-cancel' },
                issued_act: { chip: 'blue lighten-5', text: 'blue darken-2', icon: 'mdi-autorenew' },
                checked: { chip: 'green lighten-5', text: 'green darken-2', icon: 'mdi-check-decagram' },
                closed: { chip: 'grey lighten-2', text: 'grey darken-3', icon: 'mdi-close-circle-outline' }
            };
            return map[code] || { chip: 'grey lighten-3', text: 'black', icon: 'mdi-help-circle-outline' };
        },
        pad2(n) {
            return String(n).padStart(2, '0');
        },
        formatStatusDate(iso) {
            if (!iso) return '';
            const d = new Date(iso);
            return `${this.pad2(d.getHours())}:${this.pad2(d.getMinutes())} ${this.pad2(d.getDate())}.${this.pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
        },

        async fetchAttachmentBlob(attachId, opts = {}) {
            const resp = await axios.get('/prescription_ozp/getAttachmentFile', {
                params: { id: attachId, unwrapOriginal: opts.unwrapOriginal ? 1 : undefined },
                responseType: 'blob'
            });

            const rawType = (resp.headers && resp.headers['content-type']) || '';
            const disposition = (resp.headers && resp.headers['content-disposition']) || '';
            const filenameMatch = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(disposition);
            const fileName = filenameMatch ? decodeURIComponent(filenameMatch[1]) : opts.expectedName || 'document';

            let mime = rawType || 'application/octet-stream';
            const lower = (fileName || '').toLowerCase();
            if ((!mime || mime === 'application/octet-stream') && lower.endsWith('.pdf')) {
                mime = 'application/pdf';
            }

            const typedBlob = new Blob([resp.data], { type: mime });
            return { blob: typedBlob, fileName, mime };
        },
        async previewDoc(doc) {
            try {
                const name = doc?.attach_file_name ? String(doc.attach_file_name) : '';
                const unwrap = name.toLowerCase().endsWith('.cms');
                const { blob, fileName, mime } = await this.fetchAttachmentBlob(doc.record_id, {
                    unwrapOriginal: unwrap,
                    expectedName: name
                });

                if (
                    (mime && mime.includes('pdf')) ||
                    String(fileName)
                        .toLowerCase()
                        .endsWith('.pdf')
                ) {
                    const url = URL.createObjectURL(blob);
                    this.openBlobInNewTab({ url, title: this.getPrescriptionDocDisplayName(doc) || fileName || 'PDF' });
                } else {
                    await this.downloadDoc({ record_id: doc.record_id, attach_file_name: fileName, unwrapOriginal: unwrap });
                }
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось открыть документ'));
            }
        },

        async downloadDoc(doc) {
            try {
                const name = doc?.attach_file_name ? String(doc.attach_file_name) : '';

                const unwrap = !!doc?.unwrapOriginal;
                const { blob, fileName } = await this.fetchAttachmentBlob(doc.record_id, {
                    unwrapOriginal: unwrap,
                    expectedName: name
                });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = this.getPrescriptionDocDisplayName(doc) || fileName || 'document';

                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(a.href), 60000);
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось скачать документ'));
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            const dd = String(date.getDate()).padStart(2, '0');
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const yyyy = date.getFullYear();
            return `${dd}.${mm}.${yyyy}`;
        },
        async loadPrescription() {
            const id = this.$route.params.id;
            this.loadingPrescription = true;
            try {
                const response = await this.$store.dispatch('prescription_ozp/getPrescriptionOZPInfo', id);
                const sortByEventTypeRid = (events = []) => {
                    return [...events].sort((a, b) => {
                        return Number(a.event_type_rid || 0) - Number(b.event_type_rid || 0);
                    });
                };

                this.prescription = {
                    ...response,
                    prescription_events: sortByEventTypeRid(response.prescription_events || [])
                };

                const all = this.prescription?.prescription_events || [];
                const nextEdits = {};
                for (const e of all) {
                    const id = e.prescription_event_rid;
                    if (!id) continue;
                    nextEdits[id] = {
                        dead_line_fact: e.dead_line_fact || '',
                        original_dead_line_fact: e.dead_line_fact || '',
                        files: [],
                        loading: false
                    };
                }
                this.edits = nextEdits;

                await this.checkAndSetVerified();
            } catch (error) {
                console.error('Ошибка при загрузке предписания:', error);
                this.$toast?.error?.(this.$t('Ошибка при загрузке данных'));
            } finally {
                this.loadingPrescription = false;
            }
        },
        async signPrescription() {
            try {
                const id = this.prescription?.id;
                await this.$store.dispatch('prescription_ozp/signPrescriptionOZP', id);
                this.$toast?.success?.(this.$t('Предписание подписано'));
                this.loadPrescription();
            } catch (error) {
                console.error('Ошибка при подписании:', error);
                this.$toast?.error?.(this.$t('Ошибка при подписании предписания'));
            }
        },
        async signAct() {
            try {
                const id = this.prescription?.id;
                await this.$store.dispatch('prescription_ozp/signActOZP', id);
                this.$toast?.success?.(this.$t('Акт готовности подписан'));
                this.loadPrescription();
            } catch (error) {
                console.error('Ошибка при подписании:', error);
                this.$toast?.error?.(this.$t('Ошибка при подписании акта готовности'));
            }
        },
        openNoteDialog() {
            if (this.viewOnly) return;
            this.noteDraft = this.prescription?.note || '';
            this.dialogNote = true;
        },
        async saveNote() {
            if (this.viewOnly) return;
            try {
                const base = this.prescription || {};

                const ahs_events = (base.prescription_events || []).map(e => ({
                    event_type_rid: e.event_type_rid,
                    dead_line_plan: e.dead_line_plan || null,
                    to_delete: 0
                }));

                const processedModel = {
                    ...base,
                    note: (this.noteDraft || '').trim(),
                    to_delete: 0,
                    object_contracts: (base.object_contracts || []).map(o => ({
                        ...o,
                        to_delete: 0
                    })),
                    ksk_houses: (base.ksk_houses || []).map(o => ({
                        ...o,
                        to_delete: 0
                    })),
                    ahs_events
                };

                await axios.post('/prescription_ozp/postPrescriptions', {
                    nvcPrescriptionData: processedModel
                });

                this.prescription = { ...this.prescription, note: processedModel.note };
                this.dialogNote = false;
                this.$toast?.success?.(this.$t('Примечание сохранено'));
                this.loadPrescription();
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось сохранить примечание'));
            }
        },
        openSendToSmartZhkhDialog() {
            if (this.viewOnly) return;
            this.smartZhkhReason = '';
            this.dialogSendToSmartZhkh = true;
        }
    },
    data() {
        return {
            prescription: null,
            loadingPrescription: false,
            dialogCreatePlannedDeadline: false,
            dialogSignPrescription: false,
            dialogSignAct: false,
            dialogNote: false,
            noteDraft: '',
            dialogReturn: false,
            returnReason: '',
            returnTargetEvent: null,
            actionLoading: false,
            edits: {},
            expandedPanels: [],
            expandedSections: [],
            plannedDialogKey: 0,
            signDialogKey: 0,
            backNavLock: false,
            dialogCloseConfirm: false,
            dialogCloseReason: false,
            closeReasonType: '',
            closeReasonCustom: '',
            dialogSendToSmartZhkh: false,
            smartZhkhReason: ''
        };
    },
    async mounted() {
        this.loadPrescription();
        this.expandedSections = [0, 1, 2];
    },
    computed: {
        canClosePrescription() {
            return !this.viewOnly && this.isReadyForClose && this.currentStatusCode !== 'closed';
        },
        canSubmitClose() {
            if (!this.closeReasonType) return false;

            if (this.closeReasonType === 'custom') {
                const txt = (this.closeReasonCustom || '').trim();
                return txt.length > 0 && txt.length <= 200;
            }

            return true;
        },
        currentStatusCode() {
            return (
                (this.prescription?.status_code_name && String(this.prescription.status_code_name).toLowerCase()) ||
                (this.sortedStatusHistory.length ? String(this.sortedStatusHistory[this.sortedStatusHistory.length - 1].status_code_name).toLowerCase() : null)
            );
        },
        isIssuedAct() {
            return this.currentStatusCode === 'issued_act';
        },
        canEditAdditionalEvents() {
            return !this.viewOnly && !['issued_act', 'completed'].includes(this.currentStatusCode);
        },
        isBoss() {
            const translated = require('@/utils/translate.util').default(this.$store?.state?.users?.userInfo, 'full', this.$i18n?.locale || 'ru');
            const roleText = String(translated?.role_name?.role_name || '');
            return (
                roleText.includes('Инспектор') ||
                roleText.includes('Администратор ОЗП') ||
                roleText.includes('Администратор. AHS') ||
                roleText.includes('Әкімші. AHS')
            );
        },

        isInspector() {
            const translated = require('@/utils/translate.util').default(this.$store?.state?.users?.userInfo, 'full', this.$i18n?.locale || 'ru');

            const roleText = String(translated?.role_name?.role_name || '');
            return roleText.includes('Инспектор');
        },

        isAdminOZP() {
            const translated = require('@/utils/translate.util').default(this.$store?.state?.users?.userInfo, 'full', this.$i18n?.locale || 'ru');

            const roleText = String(translated?.role_name?.role_name || '');
            return roleText.includes('Администратор ОЗП') || roleText.includes('Администратор. AHS') || roleText.includes('Әкімші. AHS');
        },

        isHeadCOP() {
            const translated = require('@/utils/translate.util').default(this.$store?.state?.users?.userInfo, 'full', this.$i18n?.locale || 'ru');

            const roleText = String(translated?.role_name?.role_name || '');
            return roleText.includes('Руководитель ЦОП');
        },

        viewOnly() {
            return this.isInspector || this.isAdminOZP || this.isHeadCOP;
        },

        canSignEvent() {
            return this.isHeadCOP;
        },

        isChecked() {
            const current =
                (this.prescription?.status_code_name && String(this.prescription.status_code_name).toLowerCase()) ||
                (this.sortedStatusHistory.length ? String(this.sortedStatusHistory[this.sortedStatusHistory.length - 1].status_code_name).toLowerCase() : null);

            return current === 'checked';
        },

        isReturned() {
            const current =
                (this.prescription?.status_code_name && String(this.prescription.status_code_name).toLowerCase()) ||
                (this.sortedStatusHistory.length ? String(this.sortedStatusHistory[this.sortedStatusHistory.length - 1].status_code_name).toLowerCase() : null);

            return current === 'returned';
        },

        pendingLockId() {
            const list = this.prescription?.prescription_events || [];
            const norm = x => this.normalizeDateStr(x || '');

            for (const e of list) {
                const id = e?.prescription_event_rid;
                if (!id) continue;
                if (this.isEventExecuted(e)) continue;

                const ed = this.edits?.[id];
                if (!ed) continue;

                const hasFiles = Array.isArray(ed.files) && ed.files.length > 0;
                const dateChanged = norm(ed.dead_line_fact) && norm(ed.dead_line_fact) !== norm(e.dead_line_fact);

                if (dateChanged || hasFiles) return id;
            }
            return null;
        },

        factMaxDate() {
            const d = new Date();
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        },

        isSummer() {
            const cDate = new Date();

            const start = new Date(cDate.getFullYear(), 3, 15); // 15 апреля
            const end = new Date(cDate.getFullYear(), 7, 31); // 31 августа

            return cDate >= start && cDate <= end;
        },

        factMinDate() {
            return '2000-01-01';
        },

        factDateErrorText() {
            const fmt = ymd => {
                if (!ymd) return '';
                const [y, m, d] = ymd.split('-');
                return `${d}.${m}.${y}`;
            };
            return `Дата должна быть в диапазоне ${fmt(this.factMinDate)} — ${fmt(this.factMaxDate)}`;
        },
        hasOrgOrTech() {
            return this.orgEvents.length > 0 || this.techEvents.length > 0;
        },
        isDocumentReady() {
            return !!(this.prescription && this.prescription.record_id);
        },
        uiDisabled() {
            return this.loadingPrescription || this.actionLoading;
        },
        techEvents() {
            const list = this.prescription?.prescription_events || [];
            return list
                .filter(e => e.event_group_name === 'Технические мероприятия')
                .map(e => ({
                    _dead_line_fact: e.dead_line_fact || '',
                    _files: [],
                    _loading: false,
                    ...e
                }));
        },
        addEvents() {
            const list = this.prescription?.prescription_events || [];
            return list
                .filter(e => e.event_group_name === 'Дополнительные мероприятия')
                .map(e => ({
                    _dead_line_fact: e.dead_line_fact || '',
                    _files: [],
                    _loading: false,
                    ...e
                }));
        },
        isSignedStrict() {
            const code =
                (this.prescription?.status_code_name && String(this.prescription.status_code_name).toLowerCase()) ||
                (this.sortedStatusHistory.length ? String(this.sortedStatusHistory[this.sortedStatusHistory.length - 1].status_code_name).toLowerCase() : null);

            return ['issued', 'signed', 'completed', 'issued_act', 'checked', 'closed'].includes(code);
        },
        orgEvents() {
            const list = this.prescription?.prescription_events || [];
            return list.filter(e => e.event_group_name === 'Организационные мероприятия').map(e => ({ ...e }));
        },

        showSignedBanner() {
            const code =
                (this.prescription?.status_code_name && String(this.prescription.status_code_name).toLowerCase()) ||
                (this.sortedStatusHistory.length ? String(this.sortedStatusHistory[this.sortedStatusHistory.length - 1].status_code_name).toLowerCase() : null);

            return code === 'issued';
        },
        isSigned() {
            const current =
                (this.prescription?.status_code_name && String(this.prescription.status_code_name).toLowerCase()) ||
                (this.sortedStatusHistory.length ? String(this.sortedStatusHistory[this.sortedStatusHistory.length - 1].status_code_name).toLowerCase() : null);

            return ['issued', 'signed', 'issued_act', 'completed', 'checked', 'closed'].includes(current);
        },

        isReadyForClose() {
            const current = String(this.prescription.status_code_name).toLowerCase();

            return ['issued', 'returned', 'checked'].includes(current);
        },

        isSignedForAct() {
            const current =
                (this.prescription?.status_code_name && String(this.prescription.status_code_name).toLowerCase()) ||
                (this.sortedStatusHistory.length ? String(this.sortedStatusHistory[this.sortedStatusHistory.length - 1].status_code_name).toLowerCase() : null);

            const isTargetGroup = g => ['организационные мероприятия', 'технические мероприятия'].includes(String(g || '').toLowerCase());

            const targetEvents = (this.prescription?.prescription_events || []).filter(e => isTargetGroup(e.event_group_name));

            const allExecuted = targetEvents.length > 0 && targetEvents.every(e => this.isEventExecuted(e));

            const hasActReady =
                Array.isArray(this.prescription?.req_documents) &&
                this.prescription.req_documents.some(
                    d => typeof d?.attach_file_name === 'string' && d.attach_file_name.trim().toLowerCase() === 'акт готовности.pdf'
                );

            return current === 'checked' && allExecuted && !hasActReady;
        },
        sortedStatusHistory() {
            const arr = this.prescription?.status_history || [];

            return arr.slice().sort((a, b) => new Date(a.status_date) - new Date(b.status_date));
        },

        hasDocuments() {
            return Array.isArray(this.prescription?.req_documents) && this.prescription.req_documents.length > 0;
        },
        groupedEventsInOrder() {
            const events = this.prescription?.prescription_events || [];

            const buckets = events.reduce((acc, e) => {
                const key = e.event_group_name || 'Без категории';
                (acc[key] ||= []).push(e);
                return acc;
            }, {});

            Object.keys(buckets).forEach(k => {
                buckets[k].sort((a, b) => (Number(a.event_type_rid) || 0) - (Number(b.event_type_rid) || 0));
            });

            const order = ['Организационные мероприятия', 'Технические мероприятия', 'Дополнительные мероприятия'];

            const result = [];
            for (const title of order) {
                if (buckets[title]?.length) {
                    result.push({ title, events: buckets[title] });
                    delete buckets[title];
                }
            }
            for (const title of Object.keys(buckets)) {
                result.push({ title, events: buckets[title] });
            }
            return result;
        },
        hasEvents() {
            return this.prescription?.prescription_events?.length > 0;
        },
        canSaveNote() {
            const current = this.prescription && this.prescription.note ? this.prescription.note.trim() : '';
            const draft = (this.noteDraft || '').trim();
            return draft.length > 0 && draft !== current;
        }
    }
};
</script>

<style scoped>
.col-docs {
    min-width: 240px;
}

.events-table {
    table-layout: fixed;
    width: 100%;
}
.col-name {
    width: 50%;
}
.col-plan {
    width: 12%;
}
.col-fact {
    width: 14%;
}
.col-docs {
    width: 20%;
}
.col-actions {
    width: 80px;
    text-align: center;
}
.col-actions--org {
    text-align: right;
}

.docs-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.doc-row {
    display: flex;
    flex-direction: column;
}
.doc-name {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}
.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.doc-actions {
    display: flex;
    gap: 8px;
    margin-left: 26px;
    margin-top: 2px;
}

@media (max-width: 1440px) {
    .col-docs {
        min-width: 320px;
    }
}

.signed-hint {
    background: #f6c343;
    color: #000;
    border-radius: 4px;
    padding: 10px 12px;
}
.signed-hint__icon {
    width: 20px;
    margin-right: 8px;
}
.signed-hint__text {
    line-height: 1.4;
}

.note-dialog-card {
    border-radius: 12px;
}
.note-textarea :deep(.v-field) {
    border-radius: 10px;
}
:deep(.v-btn.rounded-pill) {
    text-transform: uppercase;
}

.boxed-section {
    position: relative;
    border: 1px solid rgba(25, 118, 210, 0.5);
    border-radius: 12px;
}
.section-title {
    position: absolute;
    top: -10px;
    left: 14px;
    padding: 0 8px;
    background: white;
    color: #1976d2;
    font-weight: 600;
    font-size: 14px;
}
.note-fab {
    position: absolute;
    top: -14px;
    right: 12px;
    background: #6b7bb2;
    color: white;
}

:deep(.v-simple-table tbody td) {
    vertical-align: middle;
}
.actions-cell,
.actions-cell--org {
    text-align: right;
    vertical-align: middle;
    padding-right: 0;
    white-space: nowrap;
    min-width: 72px;
}
.col-actions--org {
    text-align: center;
}
.actions-wrap {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
}
.actions-wrap .v-btn {
    min-width: auto;
}
.actions-cell .v-icon {
    display: inline-flex;
    vertical-align: middle;
}

.group-header {
    font-size: 18px;
    font-weight: 400;
}

.status-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}
.status-left {
    min-width: 140px;
}
.status-note {
    display: inline-block;
    width: fit-content;
    max-width: 520px;
    border-radius: 10px;
    padding: 10px 12px;
    flex: 0 0 auto;
    line-height: 1.4;
}

.status-note__text {
    display: inline;
    color: #5d4037;
}

.status-note__time {
    display: inline;
    margin-left: 10px;
    font-size: 12px;
    color: #9e9e9e;
    white-space: nowrap;
}

.status-note--returned {
    background: #fff7e6;
    border: 1px solid #f5c06a;
}

.status-note--closed {
    background: #f3f3f3;
    border: 1px solid #c7c7c7;
}

.row-locked {
    outline: 2px solid #f6c343;
    background: #fff8e1;
    transition: background 0.2s ease;
}

.status-note--issued {
    background: #eef5ff;
    border: 1px solid #9ec5fe;
}

.doc-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.doc-date {
    font-size: 11px;
    color: #9e9e9e;
    margin-top: 2px;
}

.close-btn-tooltip-wrapper {
    display: inline-block;
    margin-left: 8px;
}
.ozp-contact-info {
    margin-top: 6px;
    font-size: 13px;
    color: #555;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}
</style>
